// src/admin/TuitionFeeManager.js
import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useOutletContext } from 'react-router-dom';
import { StudentContext } from '../StudentContext';
import { firestore } from '../firebaseConfig';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import {
  subscribeTuitionSettings,
  updateTuitionSettings,
  subscribeAllTuitionFees,
  confirmTuitionBatch,
  revertTuitionConfirmationBatch
} from '../FirebaseService';
import './TuitionFeeManager.css';

const TuitionFeeManager = () => {
  const { allStudentsForOutlet } = useOutletContext();
  const { currentYearId, classMap } = useContext(StudentContext);

  const [tuitionMap, setTuitionMap] = useState({});
  const [feeSettings, setFeeSettings] = useState({ feePerStudent: 100000 });

  // 🌟 LỌC KHOẢNG NGÀY: Mặc định để trống '' để hiển thị tất cả các ngày
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'unpaid' | 'pending' | 'confirmed'
  const [searchQuery, setSearchQuery] = useState('');

  // Công tắc ẩn/hiện thiếu nhi Nghỉ luôn (Mặc định: false)
  const [showDroppedStudents, setShowDroppedStudents] = useState(false);

  // Sắp xếp cột: key mặc định 'fullName', hướng 'asc'
  const [sortConfig, setSortConfig] = useState({ key: 'fullName', direction: 'asc' });

  // Checkbox chọn nhiều em
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isProcessing, setIsProcessing] = useState(false);

  // Modal Cài đặt Mức phí
  const [isSettingOpen, setIsSettingOpen] = useState(false);
  const [tempFeeInput, setTempFeeInput] = useState(100000);

  const currentUserName = localStorage.getItem('catechistFullName') || 'Ban Hành Chánh';
  const currentUserUid = localStorage.getItem('uid') || '';

  // 1. Lắng nghe Realtime Cấu hình mức phí
  useEffect(() => {
    if (!currentYearId) return;
    const unsub = subscribeTuitionSettings(currentYearId, (settings) => {
      setFeeSettings(settings);
      setTempFeeInput(settings.feePerStudent || 100000);
    });
    return () => unsub();
  }, [currentYearId]);

  // 2. Lắng nghe Realtime Toàn bộ Học Phí Niên Khóa
  useEffect(() => {
    if (!currentYearId) return;
    const unsub = subscribeAllTuitionFees(currentYearId, (data) => {
      setTuitionMap(data || {});
    });
    return () => unsub();
  }, [currentYearId]);

  const feePerStudent = feeSettings.feePerStudent || 100000;

  // 3. Đếm số lượng thiếu nhi "Nghỉ luôn" trong phạm vi đang xem
  const droppedCount = useMemo(() => {
    return allStudentsForOutlet.filter((s) => s.status === 'Nghỉ luôn').length;
  }, [allStudentsForOutlet]);

  // 4. Thống kê Tài chính tổng quan (Tự động thích ứng theo khoảng ngày và công tắc Nghỉ luôn)
  const stats = useMemo(() => {
    const scopedStudents = allStudentsForOutlet.filter((s) => {
      if (!showDroppedStudents && s.status === 'Nghỉ luôn') return false;
      return true;
    });

    const totalStudentsInScope = scopedStudents.length;
    let unpaidCount = 0;
    let pendingCount = 0;
    let confirmedCount = 0;

    scopedStudents.forEach((s) => {
      const record = tuitionMap[s.id];
      if (!record || record.status === 'unpaid') {
        unpaidCount++;
      } else {
        const cDate = record.collectedDate || '';
        // Kiểm tra khớp khoảng ngày (Nếu không nhập thì mặc định là khớp)
        const matchesDate = (!startDate || cDate >= startDate) && (!endDate || cDate <= endDate);

        if (record.status === 'collected') {
          if (matchesDate) pendingCount++;
        } else if (record.status === 'confirmed') {
          if (matchesDate) confirmedCount++;
        }
      }
    });

    return {
      totalStudentsInScope,
      unpaidCount,
      unpaidMoney: unpaidCount * feePerStudent,
      pendingCount,
      pendingMoney: pendingCount * feePerStudent,
      confirmedCount,
      confirmedMoney: confirmedCount * feePerStudent,
    };
  }, [allStudentsForOutlet, tuitionMap, feePerStudent, startDate, endDate, showDroppedStudents]);

  // 5. Lọc danh sách học sinh
  const filteredStudents = useMemo(() => {
    const hasDateFilter = Boolean(startDate || endDate);

    return allStudentsForOutlet.filter((s) => {
      // Điều kiện 1: Công tắc ẩn thiếu nhi Nghỉ luôn
      if (!showDroppedStudents && s.status === 'Nghỉ luôn') {
        return false;
      }

      const record = tuitionMap[s.id];
      const isUnpaid = !record || record.status === 'unpaid';
      const tuitionStatus = isUnpaid ? 'unpaid' : record.status; // 'unpaid' | 'collected' | 'confirmed'

      // Điều kiện 2: Lọc theo trạng thái học phí
      if (filterStatus === 'unpaid' && tuitionStatus !== 'unpaid') return false;
      if (filterStatus === 'pending' && tuitionStatus !== 'collected') return false;
      if (filterStatus === 'confirmed' && tuitionStatus !== 'confirmed') return false;

      // Điều kiện 3: Lọc theo khoảng ngày thu của GLV
      if (hasDateFilter) {
        if (tuitionStatus === 'unpaid') {
          // Khi lọc khoảng ngày đối soát, ẩn các em chưa đóng (trừ phi người dùng cố ý chọn lọc 'unpaid')
          if (filterStatus !== 'unpaid') {
            return false;
          }
        } else {
          const cDate = record?.collectedDate || '';
          if (startDate && cDate < startDate) return false;
          if (endDate && cDate > endDate) return false;
        }
      }

      // Điều kiện 4: Lọc theo từ khóa tìm kiếm
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const fullName = (s.fullName || '').toLowerCase();
        const holyName = (s.holyName || '').toLowerCase();
        const id = (s.id || '').toLowerCase();
        const className = (classMap[record?.classId || s.classId] || s.classId || '').toLowerCase();
        const collectedBy = (record?.collectedBy || '').toLowerCase();
        const schoolStatus = (s.status || 'Còn đi học').toLowerCase();
        return (
          fullName.includes(q) ||
          holyName.includes(q) ||
          id.includes(q) ||
          className.includes(q) ||
          collectedBy.includes(q) ||
          schoolStatus.includes(q)
        );
      }

      return true;
    });
  }, [allStudentsForOutlet, tuitionMap, filterStatus, startDate, endDate, searchQuery, classMap, showDroppedStudents]);

  // 6. Sắp xếp danh sách theo Tiêu đề cột
  const sortedStudents = useMemo(() => {
    const list = [...filteredStudents];
    if (!sortConfig.key) return list;

    const getLastNamePart = (name) => {
      if (!name) return '';
      const parts = name.trim().split(' ');
      return parts[parts.length - 1].toLowerCase();
    };

    list.sort((a, b) => {
      const recordA = tuitionMap[a.id] || {};
      const recordB = tuitionMap[b.id] || {};
      let valA = '';
      let valB = '';

      switch (sortConfig.key) {
        case 'id':
          valA = a.id || '';
          valB = b.id || '';
          return sortConfig.direction === 'asc'
            ? valA.localeCompare(valB, 'vi', { numeric: true })
            : valB.localeCompare(valA, 'vi', { numeric: true });

        case 'fullName': {
          const lastA = getLastNamePart(a.fullName);
          const lastB = getLastNamePart(b.fullName);
          const cmp = lastA.localeCompare(lastB, 'vi', { sensitivity: 'base' });
          if (cmp !== 0) {
            return sortConfig.direction === 'asc' ? cmp : -cmp;
          }
          const fullCmp = (a.fullName || '').localeCompare(b.fullName || '', 'vi', { sensitivity: 'base' });
          return sortConfig.direction === 'asc' ? fullCmp : -fullCmp;
        }

        case 'schoolStatus': {
          valA = a.status || 'Còn đi học';
          valB = b.status || 'Còn đi học';
          return sortConfig.direction === 'asc'
            ? valA.localeCompare(valB, 'vi')
            : valB.localeCompare(valA, 'vi');
        }

        case 'className': {
          valA = classMap[recordA.classId || a.classId] || a.classId || '';
          valB = classMap[recordB.classId || b.classId] || b.classId || '';
          const cmp = valA.localeCompare(valB, 'vi', { sensitivity: 'base' });
          return sortConfig.direction === 'asc' ? cmp : -cmp;
        }

        case 'collectedBy':
          valA = recordA.collectedBy || '';
          valB = recordB.collectedBy || '';
          return sortConfig.direction === 'asc'
            ? valA.localeCompare(valB, 'vi', { sensitivity: 'base' })
            : valB.localeCompare(valA, 'vi', { sensitivity: 'base' });

        case 'collectedDate':
          valA = recordA.collectedDate || '';
          valB = recordB.collectedDate || '';
          return sortConfig.direction === 'asc'
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);

        case 'tuitionStatus': {
          const orderMap = { unpaid: 0, collected: 1, confirmed: 2 };
          const statusA = !recordA.status ? 0 : (orderMap[recordA.status] ?? 0);
          const statusB = !recordB.status ? 0 : (orderMap[recordB.status] ?? 0);
          return sortConfig.direction === 'asc' ? statusA - statusB : statusB - statusA;
        }

        case 'confirmedBy':
          valA = recordA.confirmedBy || '';
          valB = recordB.confirmedBy || '';
          return sortConfig.direction === 'asc'
            ? valA.localeCompare(valB, 'vi', { sensitivity: 'base' })
            : valB.localeCompare(valA, 'vi', { sensitivity: 'base' });

        default:
          return 0;
      }
    });

    return list;
  }, [filteredStudents, sortConfig, tuitionMap, classMap]);

  // Handler đổi cột sắp xếp
  const handleSort = (columnKey) => {
    setSortConfig((prev) => {
      if (prev.key === columnKey) {
        return {
          key: columnKey,
          direction: prev.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return { key: columnKey, direction: 'asc' };
    });
  };

  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="sort-icon unsorted">⇅</span>;
    }
    return sortConfig.direction === 'asc' ? (
      <span className="sort-icon asc">▲</span>
    ) : (
      <span className="sort-icon desc">▼</span>
    );
  };

  // 7. Tổng hợp theo từng Giáo lý viên trong khoảng thời gian đang lọc
  const catechistSummary = useMemo(() => {
    const summary = {};

    filteredStudents.forEach((s) => {
      const record = tuitionMap[s.id];
      if (!record || !record.collectedBy) return;

      const cName = record.collectedBy;
      if (!summary[cName]) {
        summary[cName] = {
          catechistName: cName,
          className: classMap[record.classId] || record.classId,
          totalStudents: 0,
          confirmedCount: 0,
          pendingStudentIds: []
        };
      }

      summary[cName].totalStudents++;
      if (record.status === 'confirmed') {
        summary[cName].confirmedCount++;
      } else {
        summary[cName].pendingStudentIds.push(s.id);
      }
    });

    return Object.values(summary);
  }, [filteredStudents, tuitionMap, classMap]);

  // 8. Thao tác Checkbox & Xác nhận
  const handleToggleSelect = (sId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(sId)) next.delete(sId);
      else next.add(sId);
      return next;
    });
  };

  const handleSelectAllPending = () => {
    const pendingIds = sortedStudents
      .filter((s) => tuitionMap[s.id]?.status === 'collected')
      .map((s) => s.id);

    if (selectedIds.size === pendingIds.length && pendingIds.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingIds));
    }
  };

  const handleConfirmSelected = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    const totalAmount = count * feePerStudent;

    const confirmMsg = `XÁC NHẬN BÀN GIAO:\n` +
      `- Số lượng: ${count} thiếu nhi\n` +
      `- Tổng tiền nhận: ${totalAmount.toLocaleString('vi-VN')} đ\n\n` +
      `Bạn đã nhận đủ số tiền này từ các Giáo lý viên?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      setIsProcessing(true);
      const res = await confirmTuitionBatch(
        currentYearId,
        Array.from(selectedIds),
        currentUserName,
        currentUserUid
      );
      if (res.success) {
        setSelectedIds(new Set());
      } else {
        alert('Lỗi: ' + res.message);
      }
    } catch (err) {
      alert('Lỗi: ' + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmForCatechist = async (catItem) => {
    const count = catItem.pendingStudentIds.length;
    const amount = count * feePerStudent;

    const confirmMsg = `XÁC NHẬN THU TIỀN TỪ GIÁO LÝ VIÊN:\n` +
      `- Người bàn giao: ${catItem.catechistName} (${catItem.className})\n` +
      `- Số lượng thiếu nhi: ${count} em\n` +
      `- Số tiền nhận: ${amount.toLocaleString('vi-VN')} đ\n\n` +
      `Xác nhận bạn đã nhận đủ số tiền này?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      setIsProcessing(true);
      await confirmTuitionBatch(
        currentYearId,
        catItem.pendingStudentIds,
        currentUserName,
        currentUserUid
      );
    } catch (err) {
      alert('Lỗi: ' + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDirectCollect = async (student) => {
    const confirmMsg = `THU TRỰC TIẾP TẠI VĂN PHÒNG:\n` +
      `- Thiếu nhi: ${student.holyName ? student.holyName + ' ' : ''}${student.fullName} (${student.id})\n` +
      `- Số tiền thu: ${feePerStudent.toLocaleString('vi-VN')} đ\n\n` +
      `Hệ thống sẽ ghi nhận Ban Hành Chánh thu và xác nhận hoàn tất ngay lập tức?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      setIsProcessing(true);
      const todayStr = new Date().toLocaleDateString('sv-SE');
      const docRef = doc(firestore, `academic_years/${currentYearId}/tuition_fees`, student.id);
      await setDoc(docRef, {
        studentId: student.id,
        classId: student.classId || '',
        status: 'confirmed',
        collectedBy: currentUserName,
        collectedByUid: currentUserUid,
        collectedDate: todayStr,
        collectedAt: Timestamp.now(),
        confirmedBy: currentUserName,
        confirmedByUid: currentUserUid,
        confirmedAt: Timestamp.now(),
        notes: 'BHC thu trực tiếp tại văn phòng'
      }, { merge: true });
    } catch (err) {
      alert('Lỗi: ' + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRevertSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Hoàn tác xác nhận cho ${selectedIds.size} em đã chọn về trạng thái 'Chờ BHC nhận'?`)) return;

    try {
      setIsProcessing(true);
      await revertTuitionConfirmationBatch(currentYearId, Array.from(selectedIds));
      setSelectedIds(new Set());
    } catch (err) {
      alert('Lỗi: ' + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveFeeSetting = async () => {
    try {
      const res = await updateTuitionSettings(currentYearId, tempFeeInput, currentUserName);
      if (res.success) {
        setIsSettingOpen(false);
      } else {
        alert('Lỗi: ' + res.message);
      }
    } catch (err) {
      alert('Lỗi: ' + err.message);
    }
  };

  const handleExportExcel = () => {
    const headers = [
      'STT',
      'Mã ID',
      'Tên Thánh',
      'Họ và Tên',
      'Tình Trạng Học',
      'Lớp',
      'Trạng Thái Học Phí',
      'Mức Thu (VNĐ)',
      'Người Thu Tiền',
      'Ngày Thu',
      'BHC Xác Nhận',
      'Ghi Chú'
    ];

    const rows = sortedStudents.map((s, idx) => {
      const record = tuitionMap[s.id] || {};
      let statusText = 'Chưa đóng';
      if (record.status === 'confirmed') statusText = 'BHC đã nhận tiền';
      else if (record.status === 'collected') statusText = 'Chờ nộp BHC';

      return [
        idx + 1,
        s.id,
        s.holyName || '',
        s.fullName,
        s.status || 'Còn đi học',
        classMap[record.classId] || record.classId || s.className,
        statusText,
        feePerStudent,
        record.collectedBy || '',
        record.collectedDate || '',
        record.confirmedBy || '',
        record.notes || ''
      ];
    });

    let dateSuffix = 'TatCaCacNgay';
    if (startDate && endDate) {
      dateSuffix = `${startDate}_den_${endDate}`;
    } else if (startDate) {
      dateSuffix = `tu_${startDate}`;
    } else if (endDate) {
      dateSuffix = `den_${endDate}`;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    XLSX.utils.book_append_sheet(wb, ws, 'BaoCaoHocPhi');
    XLSX.writeFile(wb, `BaoCao_HocPhi_${dateSuffix}.xlsx`);
  };

  return (
    <div className="tfm-container">
      {/* HEADER & BỘ LỌC KHOẢNG NGÀY ĐỐI SOÁT */}
      <div className="tfm-header">
        <div>
          <h2 className="tfm-title">💰 Quản Lý & Xác Nhận Học Phí Thiếu Nhi</h2>
          <p className="tfm-subtitle">
            Mức phí niên khóa: <strong>{feePerStudent.toLocaleString('vi-VN')} đ/em</strong>
            <button className="tfm-setting-btn" onClick={() => setIsSettingOpen(true)}>
              ⚙️ Cài đặt mức phí
            </button>
          </p>
        </div>

        {/* 🌟 HỘP LỌC KHOẢNG NGÀY */}
        <div className="tfm-date-picker-box">
          <div className="tfm-date-header">
            <label>📅 Ngày thu đối soát:</label>
            {(startDate || endDate) ? (
              <button
                type="button"
                className="tfm-clear-date-btn"
                onClick={() => { setStartDate(''); setEndDate(''); }}
                title="Quay về hiển thị tất cả các ngày"
              >
                ✕ Xem tất cả các ngày
              </button>
            ) : (
              <span className="tfm-all-dates-badge">Đang hiển thị tất cả</span>
            )}
          </div>
          <div className="date-range-wrap">
            <div className="date-field">
              <span>Từ ngày:</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="date-field">
              <span>Đến ngày:</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* DASHBOARD 4 Ô TỔNG KẾT TÀI CHÍNH */}
      <div className="tfm-stats-grid">
        <div className="tfm-card total">
          <div className="card-top">📦 Sĩ số đang xem</div>
          <div className="card-mid">{stats.totalStudentsInScope} <small>em</small></div>
          <div className="card-bottom">{(stats.totalStudentsInScope * feePerStudent).toLocaleString('vi-VN')} đ</div>
        </div>

        <div className="tfm-card unpaid">
          <div className="card-top">🔴 Chưa đóng tiền</div>
          <div className="card-mid text-red">{stats.unpaidCount} <small>em</small></div>
          <div className="card-bottom text-red">{stats.unpaidMoney.toLocaleString('vi-VN')} đ</div>
        </div>

        <div className="tfm-card pending">
          <div className="card-top">⏳ Tiền GLV đang giữ (Chờ nộp)</div>
          <div className="card-mid text-amber">{stats.pendingCount} <small>em</small></div>
          <div className="card-bottom text-amber">{stats.pendingMoney.toLocaleString('vi-VN')} đ</div>
        </div>

        <div className="tfm-card confirmed">
          <div className="card-top">✅ BHC đã nhận đủ tiền mặt</div>
          <div className="card-mid text-green">{stats.confirmedCount} <small>em</small></div>
          <div className="card-bottom text-green">{stats.confirmedMoney.toLocaleString('vi-VN')} đ</div>
        </div>
      </div>

      {/* DANH SÁCH GIÁO LÝ VIÊN BÀN GIAO TIỀN */}
      {catechistSummary.length > 0 && (
        <div className="tfm-section">
          <h3 className="section-title">👨‍🏫 Danh sách Giáo lý viên bàn giao tiền</h3>
          <div className="catechist-cards-grid">
            {catechistSummary.map((cat, i) => (
              <div key={i} className={`cat-summary-card ${cat.pendingStudentIds.length === 0 ? 'card-done' : ''}`}>
                <div className="cat-info">
                  <strong>{cat.catechistName}</strong>
                  <span className="cat-class-badge">{cat.className}</span>
                </div>
                <div className="cat-numbers">
                  <div>Đã thu: <strong>{cat.totalStudents} em</strong> ({(cat.totalStudents * feePerStudent).toLocaleString('vi-VN')} đ)</div>
                  <div>Chưa nộp BHC: <strong className="text-amber">{cat.pendingStudentIds.length} em</strong> ({(cat.pendingStudentIds.length * feePerStudent).toLocaleString('vi-VN')} đ)</div>
                </div>
                {cat.pendingStudentIds.length > 0 ? (
                  <button
                    className="btn-confirm-cat"
                    onClick={() => handleConfirmForCatechist(cat)}
                    disabled={isProcessing}
                  >
                    ✓ Xác nhận đã nhận {(cat.pendingStudentIds.length * feePerStudent).toLocaleString('vi-VN')} đ
                  </button>
                ) : (
                  <div className="all-handed-in">✅ Đã bàn giao đủ</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* THANH ĐIỀU KHIỂN, BỘ LỌC & CÔNG TẮC ẨN/HIỆN */}
      <div className="tfm-toolbar">
        <div className="toolbar-left">
          <input
            type="text"
            className="tfm-search"
            placeholder="🔍 Tìm theo Tên, ID hoặc Người thu..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">Tất cả ({sortedStudents.length})</option>
            <option value="unpaid">🔴 Chưa đóng ({stats.unpaidCount})</option>
            <option value="pending">⏳ Chờ BHC nhận ({stats.pendingCount})</option>
            <option value="confirmed">✅ BHC đã nhận ({stats.confirmedCount})</option>
          </select>

          {/* CÔNG TẮC ẨN / HIỆN THIẾU NHI NGHỈ LUÔN */}
          <label className="toggle-dropped-control" title="Bật để hiển thị thêm các thiếu nhi đã nghỉ luôn">
            <div className="tfm-switch">
              <input
                type="checkbox"
                checked={showDroppedStudents}
                onChange={(e) => setShowDroppedStudents(e.target.checked)}
              />
              <span className="tfm-slider"></span>
            </div>
            <span className="toggle-text">
              {showDroppedStudents ? 'Đang hiện Nghỉ luôn' : 'Ẩn Nghỉ luôn'}
              {droppedCount > 0 && <span className="dropped-badge-pill">{droppedCount}</span>}
            </span>
          </label>
        </div>

        <div className="toolbar-right">
          {selectedIds.size > 0 && (
            <>
              <button
                className="btn-batch-confirm"
                onClick={handleConfirmSelected}
                disabled={isProcessing}
              >
                ✓ Xác nhận {selectedIds.size} em ({(selectedIds.size * feePerStudent).toLocaleString('vi-VN')} đ)
              </button>
              <button
                className="btn-batch-revert"
                onClick={handleRevertSelected}
                disabled={isProcessing}
              >
                Hoàn tác ({selectedIds.size})
              </button>
            </>
          )}

          <button className="btn-export-excel" onClick={handleExportExcel}>
            📥 Xuất Excel
          </button>
        </div>
      </div>

      {/* BẢNG CHI TIẾT CÓ CỘT CỐ ĐỊNH CHO MOBILE */}
      <div className="table-responsive">
        <table className="tfm-table">
          <thead>
            <tr>
              {/* CỘT CỐ ĐỊNH 1: Checkbox */}
              <th className="sticky-col sticky-col-check">
                <input
                  type="checkbox"
                  onChange={handleSelectAllPending}
                  checked={
                    sortedStudents.filter((s) => tuitionMap[s.id]?.status === 'collected').length > 0 &&
                    selectedIds.size === sortedStudents.filter((s) => tuitionMap[s.id]?.status === 'collected').length
                  }
                  title="Chọn tất cả các em đang chờ BHC nhận"
                />
              </th>

              {/* CỘT CỐ ĐỊNH 2: Họ và Tên */}
              <th className="sticky-col sticky-col-name sortable-th" onClick={() => handleSort('fullName')}>
                Họ và Tên {renderSortIcon('fullName')}
              </th>

              {/* CỘT 3: Tình trạng đi học */}
              <th className="sortable-th" onClick={() => handleSort('schoolStatus')} style={{ minWidth: '110px' }}>
                Tình trạng học {renderSortIcon('schoolStatus')}
              </th>

              {/* CỘT 4: Mã ID */}
              <th className="sortable-th" onClick={() => handleSort('id')} style={{ width: '85px' }}>
                Mã ID {renderSortIcon('id')}
              </th>

              {/* CỘT 5: Lớp */}
              <th className="sortable-th" onClick={() => handleSort('className')}>
                Lớp {renderSortIcon('className')}
              </th>

              {/* CỘT 6: Trạng thái học phí */}
              <th className="sortable-th" onClick={() => handleSort('tuitionStatus')}>
                Trạng Thái Học Phí {renderSortIcon('tuitionStatus')}
              </th>

              {/* CỘT 7: Người thu tiền */}
              <th className="sortable-th" onClick={() => handleSort('collectedBy')}>
                Người Thu Tiền {renderSortIcon('collectedBy')}
              </th>

              {/* CỘT 8: Ngày thu */}
              <th className="sortable-th" onClick={() => handleSort('collectedDate')}>
                Ngày Thu {renderSortIcon('collectedDate')}
              </th>

              {/* CỘT 9: BHC Xác nhận */}
              <th className="sortable-th" onClick={() => handleSort('confirmedBy')}>
                BHC Xác Nhận {renderSortIcon('confirmedBy')}
              </th>

              {/* CỘT 10: Thao tác */}
              <th style={{ textAlign: 'center', minWidth: '100px' }}>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {sortedStudents.length === 0 ? (
              <tr>
                <td colSpan={10} className="empty-state">
                  Không tìm thấy thiếu nhi phù hợp với bộ lọc hiện tại.
                </td>
              </tr>
            ) : (
              sortedStudents.map((s) => {
                const record = tuitionMap[s.id] || {};
                const isConfirmed = record.status === 'confirmed';
                const isCollected = record.status === 'collected';
                const isSelected = selectedIds.has(s.id);
                const isDropped = s.status === 'Nghỉ luôn';

                return (
                  <tr
                    key={s.id}
                    className={
                      isSelected
                        ? 'row-selected'
                        : isConfirmed
                        ? 'row-confirmed'
                        : isCollected
                        ? 'row-collected'
                        : isDropped
                        ? 'row-dropped'
                        : 'row-unpaid'
                    }
                  >
                    {/* CỘT CỐ ĐỊNH 1: Checkbox */}
                    <td className="sticky-col sticky-col-check">
                      {isCollected && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelect(s.id)}
                        />
                      )}
                    </td>

                    {/* CỘT CỐ ĐỊNH 2: Họ và Tên */}
                    <td className="sticky-col sticky-col-name">
                      <div className="student-name-wrap">
                        <span className="student-holy">{s.holyName ? `${s.holyName} ` : ''}</span>
                        <strong className="student-full">{s.fullName}</strong>
                      </div>
                    </td>

                    {/* CỘT 3: Tình trạng đi học */}
                    <td>
                      {isDropped ? (
                        <span className="school-badge badge-dropped">Nghỉ luôn</span>
                      ) : (
                        <span className="school-badge badge-active">Còn đi học</span>
                      )}
                    </td>

                    {/* CỘT 4: Mã ID */}
                    <td className="cs-id-cell">{s.id}</td>

                    {/* CỘT 5: Lớp */}
                    <td>{classMap[record.classId] || record.classId || s.className}</td>

                    {/* CỘT 6: Trạng thái học phí */}
                    <td>
                      {isConfirmed ? (
                        <span className="badge badge-confirmed">✅ Đã nhận tiền</span>
                      ) : isCollected ? (
                        <span className="badge badge-pending">⏳ Chờ BHC nhận</span>
                      ) : (
                        <span className="badge badge-unpaid">🔴 Chưa đóng</span>
                      )}
                    </td>

                    {/* CỘT 7: Người thu tiền */}
                    <td>
                      {record.collectedBy ? (
                        <span className="collector-name">👤 {record.collectedBy}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>

                    {/* CỘT 8: Ngày thu */}
                    <td>
                      {record.collectedDate ? (
                        <span className="date-tag">{record.collectedDate}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>

                    {/* CỘT 9: BHC Xác nhận */}
                    <td>
                      {isConfirmed ? (
                        <span className="confirmed-officer">✔️ {record.confirmedBy}</span>
                      ) : (
                        <span className="text-muted">Chưa</span>
                      )}
                    </td>

                    {/* CỘT 10: Thao tác */}
                    <td style={{ textAlign: 'center' }}>
                      {isConfirmed ? (
                        <button
                          className="btn-sm btn-revert"
                          onClick={() => {
                            if (window.confirm(`Hoàn tác xác nhận cho em ${s.fullName}?`)) {
                              revertTuitionConfirmationBatch(currentYearId, [s.id]);
                            }
                          }}
                        >
                          Hoàn tác
                        </button>
                      ) : isCollected ? (
                        <button
                          className="btn-sm btn-confirm"
                          onClick={() => {
                            confirmTuitionBatch(currentYearId, [s.id], currentUserName, currentUserUid);
                          }}
                        >
                          Xác nhận
                        </button>
                      ) : (
                        <button
                          className="btn-sm btn-direct-collect"
                          onClick={() => handleDirectCollect(s)}
                          title="BHC thu tiền mặt trực tiếp tại văn phòng và hoàn tất"
                        >
                          BHC Thu
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* POPUP CÀI ĐẶT MỨC PHÍ */}
      {isSettingOpen && (
        <div className="tfm-modal-overlay">
          <div className="tfm-modal">
            <h3>⚙️ Cài Đặt Mức Học Phí Niên Khóa</h3>
            <p className="modal-desc">
              Số tiền này sẽ được dùng để tự động tính nhanh tổng số tiền thu được theo từng ngày và toàn niên khóa.
            </p>

            <div className="modal-body">
              <label>Số tiền đóng cho mỗi em (VNĐ):</label>
              <input
                type="number"
                step="5000"
                value={tempFeeInput}
                onChange={(e) => setTempFeeInput(Number(e.target.value) || 0)}
              />
              <div className="fee-preview">
                {tempFeeInput.toLocaleString('vi-VN')} đồng
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-save-fee" onClick={handleSaveFeeSetting}>
                Lưu cài đặt
              </button>
              <button className="btn-close-modal" onClick={() => setIsSettingOpen(false)}>
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TuitionFeeManager;